import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { useNavigate } from "react-router-dom";
import WorkTrackingAnalyticsService from "../../services/WorkTrackingAnalyticsService";
import WorkTrackingService from "../../services/WorkTrackingService";
import QuickPaymentDialog from "../Finance/QuickPaymentDialog";
import QuickCollectionDialog from "../Finance/QuickCollectionDialog";

const formatCurrency = (amount, currencyCode = "TRY") => {
  if (amount === null || amount === undefined) return null;

  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: currencyCode || "TRY",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${Number(amount).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ${currencyCode || "TRY"}`;
  }
};

export default function WorkTrackingDashboardWidget() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, failed: false, summary: {}, items: [] });
  const [expandedColumns, setExpandedColumns] = useState({ incoming: false, outgoing: false, stock: false });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [isCollectionDialogOpen, setIsCollectionDialogOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem("dashboard_hide_work_agenda");
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("dashboard_hide_work_agenda", JSON.stringify(next));
      } catch {
        // Ignore localStorage error
      }
      return next;
    });
  };

  const toggleColumn = (columnKey) => {
    setExpandedColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, failed: false }));

    try {
      const result = await WorkTrackingService.getAll({
        period: "priority",
        sort: "urgency",
        page: 1,
        pageSize: 50,
      });

      if (!result.success) throw new Error(result.message || "İşler yüklenemedi");

      setState({
        loading: false,
        failed: false,
        summary: result.summary || {},
        items: result.items || [],
      });
    } catch (error) {
      console.error("Work tracking dashboard error:", error);
      setState({ loading: false, failed: true, summary: {}, items: [] });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleQuickPayment = (item) => {
    let mappedSourceType = "SUPPLIER_DUE";
    const st = String(item.sourceType || "").toUpperCase();
    if (st.includes("CREDIT_CARD") || st.includes("CC_INSTALLMENT") || st.includes("INSTALLMENT")) {
      mappedSourceType = "CC_INSTALLMENT";
    } else if (st.includes("CHEQUE") || st.includes("CEK")) {
      mappedSourceType = "CHEQUE";
    } else if (st.includes("PROMISSORY") || st.includes("SENET")) {
      mappedSourceType = "PROMISSORY_NOTE";
    } else if (st.includes("EXPENSE") || st.includes("GIDER")) {
      mappedSourceType = "EXPENSE";
    } else if (st.includes("SUPPLIER") || st.includes("PURCHASE") || st.includes("PAYABLE")) {
      mappedSourceType = "SUPPLIER_DUE";
    }

    const paymentData = {
      payeeName: item.relatedEntityName || item.title,
      remainingAmount: item.amount,
      sourceType: mappedSourceType,
      sourceId: item.sourceId,
      supplierId: item.relatedEntityId || (mappedSourceType === "SUPPLIER_DUE" ? item.sourceId : null),
      documentNo: item.description?.match(/([A-Z0-9]{6,})/)?.[1] || "",
      branchName: item.branchName || "",
    };

    setSelectedPayment(paymentData);
    setIsPaymentDialogOpen(true);
  };

  const handleQuickCollection = (item) => {
    const customerData = {
      id: item.relatedEntityId || item.sourceId,
      name: item.relatedEntityName || item.title,
      remainingAmount: item.amount,
      balance: item.amount,
      receivableId: item.sourceId,
      description: item.description || (item.title ? `${item.title} - Tahsilat` : ""),
      branchName: item.branchName || "",
    };

    setSelectedCollection(customerData);
    setIsCollectionDialogOpen(true);
  };

  const openItem = (item) => {
    const hasPrimaryAction = item.primaryAction?.type !== "NONE";
    const normalizedCategory = String(item.category || "").toLowerCase();
    const safeCategory = ["collection", "payment", "document", "installment", "stock"]
      .includes(normalizedCategory)
      ? normalizedCategory
      : "all";
    void WorkTrackingAnalyticsService.track({
      eventType: hasPrimaryAction ? "primary_action_click" : "detail_click",
      workItemType: item.category,
      sourceType: item.sourceType,
      selectedPeriod: "priority",
      selectedCategory: "dashboard",
    });
    navigate(hasPrimaryAction
      ? item.primaryAction?.url || item.detailUrl || "/work-tracking"
      : `/work-tracking?category=${safeCategory}`);
  };

  // Group items into 3 clear business streams:
  const incomingItems = state.items.filter((item) => {
    const cat = String(item.category || "").toUpperCase();
    const st = String(item.sourceType || "").toUpperCase();
    const dir = String(item.direction || "").toUpperCase();
    return dir === "INCOMING" || cat === "COLLECTION" || st === "CUSTOMER_RECEIVABLE";
  });

  const outgoingItems = state.items.filter((item) => {
    const cat = String(item.category || "").toUpperCase();
    const st = String(item.sourceType || "").toUpperCase();
    const dir = String(item.direction || "").toUpperCase();
    return (
      dir === "OUTGOING" ||
      cat === "PAYMENT" ||
      cat === "INSTALLMENT" ||
      st === "SUPPLIER_PAYABLE" ||
      st === "CREDIT_CARD_INSTALLMENT"
    );
  });

  const otherItems = state.items.filter((item) => {
    const isInc = incomingItems.some((x) => x.id === item.id);
    const isOut = outgoingItems.some((x) => x.id === item.id);
    return !isInc && !isOut;
  });

  const incomingTotal = incomingItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const outgoingTotal = outgoingItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const totalPending = incomingItems.length + outgoingItems.length + otherItems.length;

  const visibleIncoming = expandedColumns.incoming ? incomingItems : incomingItems.slice(0, 5);
  const visibleOutgoing = expandedColumns.outgoing ? outgoingItems : outgoingItems.slice(0, 5);
  const visibleOther = expandedColumns.stock ? otherItems : otherItems.slice(0, 5);

  if (state.loading) {
    return (
      <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton shape="circle" size="2.5rem" />
            <div>
              <Skeleton width="12rem" height="1.25rem" className="mb-1.5" />
              <Skeleton width="18rem" height="0.75rem" />
            </div>
          </div>
          <Skeleton width="8rem" height="2rem" borderRadius="10px" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map((col) => (
            <div key={col} className="space-y-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-950/40">
              <Skeleton width="60%" height="1.2rem" />
              <Skeleton height="3.8rem" borderRadius="12px" />
              <Skeleton height="3.8rem" borderRadius="12px" />
              <Skeleton height="3.8rem" borderRadius="12px" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (state.failed) {
    return (
      <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex !h-10 !w-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-300">
              <i className="pi pi-exclamation-circle text-base" />
            </span>
            <div>
              <h2 className="m-0 text-base font-black text-slate-900 dark:text-white">Bugünün İş Ajandası</h2>
              <p className="mb-0 mt-0.5 text-xs font-semibold text-slate-500 dark:text-slate-400">Veriler şu anda yüklenemedi.</p>
            </div>
          </div>
          <Button label="Tekrar Dene" icon="pi pi-refresh" outlined onClick={load} className="p-button-sm !rounded-xl !font-black !border-none" />
        </div>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-all dark:border-slate-800 dark:bg-slate-900 p-5 sm:p-6">
      {/* Ambient Glows */}
      <div className="pointer-events-none absolute -right-24 -top-28 h-64 w-64 rounded-full bg-emerald-100/30 blur-3xl dark:bg-emerald-950/20" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-20 top-20 h-48 w-48 rounded-full bg-blue-50/40 blur-3xl dark:bg-blue-950/15" aria-hidden="true" />

      {/* 1. TOP HEADER */}
      <div className={`relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${
        isCollapsed ? "" : "border-b border-slate-100 dark:border-slate-800/80 pb-4 mb-5"
      }`}>
        <div className="flex items-center gap-3">
          <div className="flex !h-10 !w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white shadow-md shadow-indigo-950/20">
            <i className="pi pi-calendar-clock text-base text-emerald-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="m-0 text-base sm:text-lg font-black tracking-tight text-slate-900 dark:text-white">
                Bugünün İş Ajandası & Öncelikli Takip
              </h2>
              {totalPending > 0 ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-0.5 text-[10px] font-black text-rose-700 dark:bg-rose-950/60 dark:text-rose-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
                  {totalPending} acil işlem
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-black text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                  <i className="pi pi-check text-[9px]" /> Tüm işler tamam
                </span>
              )}
            </div>
            <p className="mb-0 mt-0.5 text-xs font-semibold text-slate-500 dark:text-slate-400">
              Bugün kasanıza girecek paralar, yapılması gereken ödemeler ve kritik stok uyarıları
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleCollapse}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white/90 text-slate-600 text-xs font-bold shadow-2xs transition-all hover:bg-slate-50 hover:text-slate-900 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 cursor-pointer"
            title={isCollapsed ? "Ajandayı Göster" : "Ajandayı Gizle"}
          >
            <i className={`pi ${isCollapsed ? "pi-eye" : "pi-eye-slash"} text-xs`} />
            <span className="text-[11px] font-bold">{isCollapsed ? "Ajandayı Göster" : "Ajandayı Gizle"}</span>
          </button>
          <Button
            type="button"
            icon="pi pi-refresh"
            className="p-button-text p-button-sm !h-8 !w-8 !p-0 !text-slate-400 hover:!text-slate-700 dark:!text-slate-500 dark:hover:!text-white !border-none cursor-pointer"
            title="Yenile"
            onClick={load}
          />
          <Button
            type="button"
            label="Tüm İşleri Aç"
            icon="pi pi-arrow-up-right"
            iconPos="right"
            className="!rounded-xl !bg-indigo-50 hover:!bg-indigo-100 !px-3.5 !py-1.5 !text-xs !font-black !text-indigo-600 dark:!bg-slate-800 dark:!text-indigo-400 dark:hover:!bg-slate-700 !border-none transition-all cursor-pointer"
            onClick={() => navigate("/work-tracking")}
          />
        </div>
      </div>

      {/* 2. THREE DEDICATED STREAM COLUMNS (WHEN EXPANDED) */}
      {!isCollapsed && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">

          {/* COLUMN 1: 🟢 TAHSİLATLAR (Gelecek Para) */}
          <div className="flex flex-col rounded-2xl bg-emerald-50/40 dark:bg-emerald-950/15 p-3.5 sm:p-4 border border-emerald-100/70 dark:border-emerald-900/30">
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-emerald-100 dark:border-emerald-900/30">
              <div className="flex items-center gap-2">
                <span className="flex !h-7 !w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-500 text-white shadow-xs">
                  <i className="pi pi-arrow-down-left text-[11px]" />
                </span>
                <div>
                  <h3 className="m-0 text-xs font-black uppercase tracking-wider text-emerald-900 dark:text-emerald-300">
                    Tahsilatlar
                  </h3>
                  <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400">
                    {incomingItems.length} Alacak
                  </span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-[9px] font-bold uppercase text-emerald-600 dark:text-emerald-400 block">Beklenen</span>
                <span className="text-xs font-black text-emerald-900 dark:text-emerald-200">
                  +{formatCurrency(incomingTotal)}
                </span>
              </div>
            </div>

            {/* Incoming Items List */}
            <div className={`space-y-2.5 ${expandedColumns.incoming ? "max-h-[500px] overflow-y-auto pr-1" : ""}`}>
              {incomingItems.length > 0 ? (
                <>
                  {visibleIncoming.map((item) => (
                    <div
                      key={item.id}
                      className="p-3 rounded-xl bg-white dark:bg-slate-800 shadow-xs hover:shadow-md transition-all duration-200 border border-slate-200/70 dark:border-slate-700/80 border-l-[5px] !border-l-emerald-500 flex items-center justify-between gap-3 group"
                    >
                      <div className="min-w-0 flex-1">
                        <strong className="block text-xs sm:text-[13px] font-black text-slate-800 dark:text-white truncate">
                          {item.title}
                        </strong>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[9px] font-black ${
                            item.daysRemaining !== null && item.daysRemaining < 0
                              ? "bg-rose-100 text-rose-800 dark:bg-rose-950/80 dark:text-rose-200"
                              : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-200"
                          }`}>
                            {item.daysRemaining !== null && item.daysRemaining < 0
                              ? `⚠️ ${Math.abs(item.daysRemaining)} Gün Gecikti`
                              : "⏰ Bugün Vadesi"}
                          </span>
                          {item.branchName && (
                            <span className="text-[10px] font-bold text-slate-400 truncate">• {item.branchName}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="text-xs font-black text-emerald-600 dark:text-emerald-400">
                          +{formatCurrency(item.amount, item.currencyCode)}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleQuickCollection(item)}
                          className="border-none py-1 px-2.5 rounded-lg bg-emerald-50 hover:bg-emerald-600 text-emerald-700 hover:text-white dark:bg-emerald-950/60 dark:text-emerald-300 dark:hover:bg-emerald-600 dark:hover:text-white font-black text-[10px] flex items-center gap-1 transition-all cursor-pointer active:scale-95 shadow-2xs"
                        >
                          <span>Tahsil Et</span>
                          <i className="pi pi-arrow-down-left text-[9px]" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Inline Expand Toggle */}
                  {incomingItems.length > 5 && (
                    <button
                      type="button"
                      onClick={() => toggleColumn("incoming")}
                      className="w-full py-2 px-3 rounded-xl bg-emerald-100/70 hover:bg-emerald-200/80 text-emerald-800 dark:bg-emerald-950/50 dark:hover:bg-emerald-900/60 dark:text-emerald-200 text-[11px] font-black flex items-center justify-center gap-1.5 transition-all cursor-pointer border-none shadow-2xs"
                    >
                      <span>{expandedColumns.incoming ? "Daha Az Göster" : `+${incomingItems.length - 5} Diğer Tahsilatı Göster`}</span>
                      <i className={`pi ${expandedColumns.incoming ? "pi-chevron-up" : "pi-chevron-down"} text-[9px]`} />
                    </button>
                  )}

                  {/* Natural flow completion card if few items */}
                  {incomingItems.length < 4 && (
                    <div className="py-2.5 px-3 rounded-xl bg-emerald-100/50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-300 text-xs font-bold flex items-center justify-center gap-2 text-center border border-emerald-200/50 dark:border-emerald-900/30">
                      <i className="pi pi-check-circle text-emerald-600 text-sm shrink-0" />
                      <span>Başka vadesi gelen tahsilatınız yok</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="py-8 flex flex-col items-center justify-center text-center text-emerald-700 dark:text-emerald-400">
                  <i className="pi pi-check-circle text-2xl mb-1.5 text-emerald-500" />
                  <span className="text-xs font-black">Tahsilat bekleyen borç yok!</span>
                  <span className="text-[10px] opacity-75 mt-0.5">Tüm müşteri bakiyeleri güncel.</span>
                </div>
              )}
            </div>
          </div>

          {/* COLUMN 2: 🔴 ÖDEMELER (Çıkacak Para) */}
          <div className="flex flex-col rounded-2xl bg-rose-50/40 dark:bg-rose-950/15 p-3.5 sm:p-4 border border-rose-100/70 dark:border-rose-900/30">
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-rose-100 dark:border-rose-900/30">
              <div className="flex items-center gap-2">
                <span className="flex !h-7 !w-7 shrink-0 items-center justify-center rounded-lg bg-rose-500 text-white shadow-xs">
                  <i className="pi pi-arrow-up-right text-[11px]" />
                </span>
                <div>
                  <h3 className="m-0 text-xs font-black uppercase tracking-wider text-rose-900 dark:text-rose-300">
                    Ödemeler
                  </h3>
                  <span className="text-[10px] font-bold text-rose-700 dark:text-rose-400">
                    {outgoingItems.length} Borç & Masraf
                  </span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-[9px] font-bold uppercase text-rose-600 dark:text-rose-400 block">Ödenecek</span>
                <span className="text-xs font-black text-rose-900 dark:text-rose-200">
                  -{formatCurrency(outgoingTotal)}
                </span>
              </div>
            </div>

            {/* Outgoing Items List */}
            <div className={`space-y-2.5 ${expandedColumns.outgoing ? "max-h-[500px] overflow-y-auto pr-1" : ""}`}>
              {outgoingItems.length > 0 ? (
                <>
                  {visibleOutgoing.map((item) => (
                    <div
                      key={item.id}
                      className="p-3 rounded-xl bg-white dark:bg-slate-800 shadow-xs hover:shadow-md transition-all duration-200 border border-slate-200/70 dark:border-slate-700/80 border-l-[5px] !border-l-rose-500 flex items-center justify-between gap-3 group"
                    >
                      <div className="min-w-0 flex-1">
                        <strong className="block text-xs sm:text-[13px] font-black text-slate-800 dark:text-white truncate">
                          {item.title}
                        </strong>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[9px] font-black ${
                            item.daysRemaining !== null && item.daysRemaining < 0
                              ? "bg-rose-100 text-rose-800 dark:bg-rose-950/80 dark:text-rose-200"
                              : "bg-amber-100 text-amber-900 dark:bg-amber-950/80 dark:text-amber-200"
                        }`}>
                          {item.daysRemaining !== null && item.daysRemaining < 0
                            ? `⚠️ ${Math.abs(item.daysRemaining)} Gün Gecikti`
                            : "⏰ Bugün Son Gün"}
                        </span>
                        {item.branchName && (
                          <span className="text-[10px] font-bold text-slate-400 truncate">• {item.branchName}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-xs font-black text-rose-600 dark:text-rose-400">
                        -{formatCurrency(item.amount, item.currencyCode)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleQuickPayment(item)}
                        className="border-none py-1 px-2.5 rounded-lg bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white dark:bg-rose-950/60 dark:text-rose-300 dark:hover:bg-rose-600 dark:hover:text-white font-black text-[10px] flex items-center gap-1 transition-all cursor-pointer active:scale-95 shadow-2xs"
                      >
                        <span>Ödeme Yap</span>
                        <i className="pi pi-credit-card text-[9px]" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Inline Expand Toggle */}
                {outgoingItems.length > 5 && (
                  <button
                    type="button"
                    onClick={() => toggleColumn("outgoing")}
                    className="w-full py-2 px-3 rounded-xl bg-rose-100/70 hover:bg-rose-200/80 text-rose-800 dark:bg-rose-950/50 dark:hover:bg-rose-900/60 dark:text-rose-200 text-[11px] font-black flex items-center justify-center gap-1.5 transition-all cursor-pointer border-none shadow-2xs"
                  >
                    <span>{expandedColumns.outgoing ? "Daha Az Göster" : `+${outgoingItems.length - 5} Diğer Ödemeyi Göster`}</span>
                    <i className={`pi ${expandedColumns.outgoing ? "pi-chevron-up" : "pi-chevron-down"} text-[9px]`} />
                  </button>
                )}

                {/* Natural flow completion card if few items */}
                {outgoingItems.length < 4 && (
                  <div className="py-2.5 px-3 rounded-xl bg-rose-100/50 dark:bg-rose-950/30 text-rose-800 dark:text-rose-200 text-xs font-bold flex items-center justify-center gap-2 text-center border border-rose-200/50 dark:border-rose-900/30">
                    <i className="pi pi-check-circle text-rose-600 text-sm shrink-0" />
                    <span>Başka vadesi gelen ödemeniz yok</span>
                  </div>
                )}
              </>
            ) : (
              <div className="py-8 flex flex-col items-center justify-center text-center text-rose-700 dark:text-rose-400">
                <i className="pi pi-check-circle text-2xl mb-1.5 text-emerald-500" />
                <span className="text-xs font-black">Bugün yapılacak ödeme yok!</span>
                <span className="text-[10px] opacity-75 mt-0.5">Vadesi gelen borç bulunmuyor.</span>
              </div>
            )}
          </div>
        </div>

        {/* COLUMN 3: 🟣 STOK & DİĞER UYARILAR */}
        <div className="flex flex-col rounded-2xl bg-violet-50/40 dark:bg-violet-950/15 p-3.5 sm:p-4 border border-violet-100/70 dark:border-violet-900/30">
          <div className="flex items-center justify-between pb-3 mb-3 border-b border-violet-100 dark:border-violet-900/30">
            <div className="flex items-center gap-2">
              <span className="flex !h-7 !w-7 shrink-0 items-center justify-center rounded-lg bg-violet-600 text-white shadow-xs">
                <i className="pi pi-bell text-[11px]" />
              </span>
              <div>
                <h3 className="m-0 text-xs font-black uppercase tracking-wider text-violet-900 dark:text-violet-300">
                  Stok & Evrak
                </h3>
                <span className="text-[10px] font-bold text-violet-700 dark:text-violet-400">
                  {otherItems.length} Bildirim
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-[9px] font-bold uppercase text-violet-600 dark:text-violet-400 block">Durum</span>
              <span className="text-xs font-black text-violet-900 dark:text-violet-200">
                {otherItems.length > 0 ? "Aksiyon Bekliyor" : "Stabil"}
              </span>
            </div>
          </div>

          {/* Other Items List */}
          <div className={`space-y-2.5 ${expandedColumns.stock ? "max-h-[500px] overflow-y-auto pr-1" : ""}`}>
            {otherItems.length > 0 ? (
              <>
                {visibleOther.map((item) => {
                  const isStock = item.sourceType === "CRITICAL_STOCK" || item.category === "STOCK";
                  const isDoc = item.sourceType === "CHEQUE" || item.sourceType === "PROMISSORY_NOTE";

                  return (
                    <div
                      key={item.id}
                      className={`p-3 rounded-xl bg-white dark:bg-slate-800 shadow-xs hover:shadow-md transition-all duration-200 border border-slate-200/70 dark:border-slate-700/80 border-l-[5px] flex items-center justify-between gap-3 group ${
                        isStock ? "!border-l-amber-500" : "!border-l-violet-500"
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <strong className="block text-xs sm:text-[13px] font-black text-slate-800 dark:text-white truncate">
                          {item.title}
                        </strong>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[9px] font-black ${
                            isStock
                              ? "bg-amber-100 text-amber-900 dark:bg-amber-950/80 dark:text-amber-200"
                              : "bg-violet-100 text-violet-900 dark:bg-violet-950/80 dark:text-violet-200"
                          }`}>
                            {isStock ? "📦 Kritik Stok" : isDoc ? "📄 Çek / Senet" : "⚡ Acil İşlem"}
                          </span>
                          {item.branchName && (
                            <span className="text-[10px] font-bold text-slate-400 truncate">• {item.branchName}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {item.amount ? (
                          <span className="text-xs font-black text-violet-600 dark:text-violet-400">
                            {formatCurrency(item.amount, item.currencyCode)}
                          </span>
                        ) : isStock ? (
                          <span className="text-xs font-black text-rose-600 dark:text-rose-400">
                            0 Adet Kaldı
                          </span>
                        ) : null}

                        <button
                          type="button"
                          onClick={() => openItem(item)}
                          className={`border-none py-1 px-2.5 rounded-lg font-black text-[10px] flex items-center gap-1 transition-all cursor-pointer active:scale-95 shadow-2xs ${
                            isStock
                              ? "bg-amber-50 hover:bg-amber-600 text-amber-700 hover:text-white dark:bg-amber-950/60 dark:text-amber-300 dark:hover:bg-amber-600 dark:hover:text-white"
                              : "bg-violet-50 hover:bg-violet-600 text-violet-700 hover:text-white dark:bg-violet-950/60 dark:text-violet-300 dark:hover:bg-violet-600 dark:hover:text-white"
                          }`}
                        >
                          <span>{isStock ? "Stok Girişi" : "Evrakı Gör"}</span>
                          <i className="pi pi-arrow-right text-[8px]" />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Inline Expand Toggle */}
                {otherItems.length > 5 && (
                  <button
                    type="button"
                    onClick={() => toggleColumn("stock")}
                    className="w-full py-2 px-3 rounded-xl bg-violet-100/70 hover:bg-violet-200/80 text-violet-800 dark:bg-violet-950/50 dark:hover:bg-violet-900/60 dark:text-violet-200 text-[11px] font-black flex items-center justify-center gap-1.5 transition-all cursor-pointer border-none shadow-2xs"
                  >
                    <span>{expandedColumns.stock ? "Daha Az Göster" : `+${otherItems.length - 5} Diğer Bildirimi Göster`}</span>
                    <i className={`pi ${expandedColumns.stock ? "pi-chevron-up" : "pi-chevron-down"} text-[9px]`} />
                  </button>
                )}

                {/* Natural flow completion card if few items */}
                {otherItems.length < 4 && (
                  <div className="py-2.5 px-3 rounded-xl bg-violet-100/50 dark:bg-violet-950/30 text-violet-800 dark:text-violet-200 text-xs font-bold flex items-center justify-center gap-2 text-center border border-violet-200/50 dark:border-violet-900/30">
                    <i className="pi pi-check-circle text-violet-600 text-sm shrink-0" />
                    <span>Diğer tüm stok seviyeleri güvende</span>
                  </div>
                )}
              </>
            ) : (
              <div className="py-8 flex flex-col items-center justify-center text-center text-violet-700 dark:text-violet-400">
                <i className="pi pi-check-circle text-2xl mb-1.5 text-emerald-500" />
                <span className="text-xs font-black">Tüm stok ve evraklar normal!</span>
                <span className="text-[10px] opacity-75 mt-0.5">Kritik stok seviyesinde ürün yok.</span>
              </div>
            )}
          </div>
        </div>

      </div>
    )}

      {/* REUSABLE QUICK PAYMENT DIALOG */}
      <QuickPaymentDialog
        visible={isPaymentDialogOpen}
        onHide={() => setIsPaymentDialogOpen(false)}
        payment={selectedPayment}
        onSuccess={() => {
          load();
        }}
      />

      {/* REUSABLE QUICK COLLECTION DIALOG */}
      <QuickCollectionDialog
        visible={isCollectionDialogOpen}
        onHide={() => setIsCollectionDialogOpen(false)}
        customer={selectedCollection}
        onSuccess={() => {
          load();
        }}
      />
    </section>
  );
}
